import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getGuestOrder, requestGuestReturn } from '../../../fetchers/return';
import Footer from '../../../components/Footer';
import FormErrorMessage from '../../../components/FormErrorMessage';
import FormInput from '../../../components/FormInput';
import FormTextArea from '../../../components/FormTextArea';
import Menu from '../../../components/Menu/Menu';
import SecondaryButtonBig from '../../../components/SecondaryButtonBig';
import Spinner from '../../../components/Spinner';
import { useAlert } from '../../../context/AlertProvider';
import { getToken, isLogged } from '../../../helpers/auth';
import { getLangCountry } from '../../../helpers/languages';
import localizedRoute from '../../../utils/localizedRoute';

interface IReturnData {
  id_order: string;
  email: string;
}

const ReturnPage: React.FC = () => {
  const alert = useAlert();
  const token = getToken();
  const router = useRouter();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { lang, country } = getLangCountry(router.query);
  const logged = isLogged();

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset
  } = useForm<IReturnData>({
    defaultValues: {
      id_order: '',
      email: ''
    }
  });

  useEffect(() => {
    if (logged && router.isReady) {
      router.replace(`/${lang}_${country}/profile/withdrawals`);
    }
  }, [router.query]);

  const onSubmit = async (values: IReturnData, event: any) => {
    try {
      setLoading(true);

      const order = await getGuestOrder({ token, lang, country, ...values });

      if (order.return === 0) {
        return alert.error(t('RETURN_NOT_ELIGIBLE'));
      }

      reset();
      event.target.reset();
      router.push(
        `/${lang}_${country}/return/${order.id}?email=${values.email}`
      );
    } catch (error: any) {
      if (error.response.status === 404) {
        alert.error(t('WRONG_ORDER_OR_EMAIL'));
      } else {
        alert.error(t('GENERIC_ERROR'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Menu />
      <div className="py-12 bg-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <div className="flex items-left justify-between mb-3 flex-col md:flex-row md:items-center">
              <h3 className="text-3xl font-sans">{t('WITHDRAWAL_REQUEST')}</h3>
            </div>

            <p className="text-gray-500">{t('WITHDRAWAL_TEXT')}</p>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div>
                  <FormInput
                    id="email"
                    type="email"
                    label="Email"
                    placeholder={t('MAIL_ADDRESS')}
                    invalid={Boolean(errors.email)}
                    {...register('email', {
                      required: t('REQUIRED_FIELD') as string,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                        message: t('INVALID_EMAIL')
                      }
                    })}
                  />

                  <FormErrorMessage errors={errors} name="email" />
                </div>

                <FormInput
                  id="id_order"
                  type="text"
                  label={t('ORDER_NUMBER')}
                  placeholder={t('ORDER_NUMBER')}
                  invalid={Boolean(errors.id_order)}
                  {...{
                    ...register('id_order', {
                      required: t('REQUIRED_FIELD') as string
                    })
                  }}
                />

                <FormErrorMessage errors={errors} name="id_order" />
              </div>

              <div className="mt-6 max-w-xs">
                {loading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="w-full">
                    <SecondaryButtonBig text={t('SEND_REQUEST')} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ReturnPage;
